import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { getActiveUser, logoutUser } from './authService';
import { getStoredProjects } from './storageService';

const ClientDashboard = ({ onOpenAuth }) => {
  const history = useHistory();
  const [currentUser, setCurrentUser] = useState(null);
  const [myProjects, setMyProjects] = useState([]);

  const loadDashboardData = () => {
    const user = getActiveUser();
    if (!user) {
      // If not logged in, redirect to home or open auth
      setCurrentUser(null);
      return;
    }
    setCurrentUser(user);

    // Filter projects matching this user's email or all projects if admin
    const all = getStoredProjects();
    if (user.role === 'admin') {
      setMyProjects(all);
    } else {
      const userProjects = all.filter(p => 
        p.client?.email?.toLowerCase() === user.email.toLowerCase() ||
        p.client?.name?.toLowerCase() === user.name.toLowerCase()
      );
      setMyProjects(userProjects);
    }
  };

  useEffect(() => {
    loadDashboardData();
    window.addEventListener('auth_state_changed', loadDashboardData);
    window.addEventListener('projects_updated', loadDashboardData);
    return () => {
      window.removeEventListener('auth_state_changed', loadDashboardData);
      window.removeEventListener('projects_updated', loadDashboardData);
    };
  }, []);

  const handleSignOut = () => {
    logoutUser();
    history.push('/');
  };

  if (!currentUser) {
    return (
      <div style={{ background: 'var(--bg-secondary)', minHeight: '85vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div className="workshop-card" style={{ maxWidth: '460px', width: '100%', padding: '3rem 2rem', backgroundColor: '#ffffff', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>👤</div>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 800, marginBottom: '0.6rem', color: '#0f172a' }}>
            Client Portal Sign In
          </h2>
          <p style={{ color: '#64748b', fontSize: '0.94rem', lineHeight: '1.6', marginBottom: '2rem' }}>
            Sign in to your client dashboard to track your build requests, review formal Statements of Work, and approve staged milestone releases.
          </p>
          <button
            onClick={() => { if (onOpenAuth) onOpenAuth('login'); }}
            className="btn-primary-blue"
            style={{ width: '100%', justifyContent: 'center', padding: '0.85rem' }}
          >
            Sign In / Register →
          </button>
        </div>
      </div>
    );
  }

  const proposalsReadyCount = myProjects.filter(p => p.status === 'Proposal Ready').length;
  const inProductionCount = myProjects.filter(p => p.status === 'Signed & In Production').length;
  const totalInvestment = myProjects.reduce((sum, p) => sum + (p.proposal?.totalEstimate || 0), 0);

  return (
    <div style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', minHeight: '100vh', paddingTop: '3rem', paddingBottom: '6rem' }}>
      <div className="container-custom">
        {/* Dashboard Header Bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1.5rem', marginBottom: '2.5rem' }}>
          <div>
            <div className="pill-badge pill-blue" style={{ marginBottom: '0.5rem' }}>
              CLIENT COMMAND CENTER
            </div>
            <h1 style={{ fontSize: '2.4rem', margin: 0, fontWeight: 900 }}>
              Welcome back, {currentUser.name}!
            </h1>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.96rem', marginTop: '0.3rem' }}>
              {currentUser.email} &bull; {currentUser.organization || 'Individual Account'}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'center' }}>
            <Link to="/#top" className="btn-primary-blue" style={{ padding: '0.65rem 1.2rem', fontSize: '0.9rem' }}>
              + Start New Build Request
            </Link>
            {currentUser.role === 'admin' && (
              <Link to="/admin" className="btn-outline-blue" style={{ padding: '0.65rem 1.2rem', fontSize: '0.9rem' }}>
                🔐 Admin Studio
              </Link>
            )}
            <button
              onClick={handleSignOut}
              style={{
                background: 'transparent',
                border: '1px solid var(--border-subtle)',
                color: '#64748b',
                padding: '0.6rem 1rem',
                borderRadius: '8px',
                fontSize: '0.86rem',
                cursor: 'pointer'
              }}
            >
              Sign Out
            </button>
          </div>
        </div>

        {/* Client KPI Overview Cards */}
        <div className="row mb-4">
          <div className="col-md-3 col-sm-6 mb-3">
            <div className="workshop-card" style={{ padding: '1.5rem', backgroundColor: '#ffffff' }}>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>
                Total Projects
              </div>
              <div style={{ fontSize: '2.2rem', fontWeight: 900, color: 'var(--accent-blue)', marginTop: '0.2rem', fontFamily: 'Outfit' }}>
                {myProjects.length}
              </div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                Active &amp; completed requests
              </div>
            </div>
          </div>

          <div className="col-md-3 col-sm-6 mb-3">
            <div className="workshop-card card-blue" style={{ padding: '1.5rem', backgroundColor: '#ffffff' }}>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>
                Proposals for Review
              </div>
              <div style={{ fontSize: '2.2rem', fontWeight: 900, color: '#1d4ed8', marginTop: '0.2rem', fontFamily: 'Outfit' }}>
                {proposalsReadyCount}
              </div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                Awaiting digital sign-off
              </div>
            </div>
          </div>

          <div className="col-md-3 col-sm-6 mb-3">
            <div className="workshop-card card-emerald" style={{ padding: '1.5rem', backgroundColor: '#ffffff' }}>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>
                In Production
              </div>
              <div style={{ fontSize: '2.2rem', fontWeight: 900, color: '#059669', marginTop: '0.2rem', fontFamily: 'Outfit' }}>
                {inProductionCount}
              </div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                Active engineering sprints
              </div>
            </div>
          </div>

          <div className="col-md-3 col-sm-6 mb-3">
            <div className="workshop-card" style={{ padding: '1.5rem', backgroundColor: '#ffffff' }}>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>
                Committed Scope
              </div>
              <div style={{ fontSize: '2.2rem', fontWeight: 900, color: '#0f172a', marginTop: '0.2rem', fontFamily: 'Outfit' }}>
                ${totalInvestment.toLocaleString()}
              </div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                Total deliverable investment
              </div>
            </div>
          </div>
        </div>

        {/* Projects List */}
        <div className="workshop-card" style={{ padding: '2rem', backgroundColor: '#ffffff' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.6rem' }}>
            <h3 style={{ fontSize: '1.35rem', margin: 0, fontWeight: 800 }}>
              My Build Portfolio
            </h3>
          </div>

          {myProjects.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '4rem 1rem', color: '#94a3b8' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.8rem' }}>🚀</div>
              <h4>No Projects Submitted Yet</h4>
              <p style={{ maxWidth: '420px', margin: '0.5rem auto 1.5rem auto', fontSize: '0.92rem' }}>
                Have an idea for a web application, personal utility, cloud automation, or custom timber piece?
              </p>
              <Link to="/#top" className="btn-primary-blue">
                Submit Your First Build Request →
              </Link>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
              {myProjects.map((proj) => {
                const hasProposal = Boolean(proj.proposal);
                const isSigned = Boolean(proj.signature);

                return (
                  <div
                    key={proj.id}
                    style={{
                      padding: '1.6rem',
                      borderRadius: '16px',
                      border: '1px solid #e2e8f0',
                      backgroundColor: '#f8fafc',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '1.2rem'
                    }}
                  >
                    <div style={{ flex: '1 1 450px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', marginBottom: '0.4rem' }}>
                        <span style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '0.95rem', color: 'var(--accent-blue)' }}>
                          {proj.id}
                        </span>
                        <span className="pill-badge pill-blue" style={{ fontSize: '0.72rem' }}>
                          {proj.category}
                        </span>
                        <span style={{
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          padding: '0.15rem 0.55rem',
                          borderRadius: '999px',
                          backgroundColor: proj.status === 'Needs More Info' ? '#fef3c7' : isSigned ? '#ecfdf5' : hasProposal ? '#eff6ff' : '#fffbeb',
                          color: proj.status === 'Needs More Info' ? '#b45309' : isSigned ? '#059669' : hasProposal ? '#1d4ed8' : '#b45309',
                          border: proj.status === 'Needs More Info' ? '1px solid #fde68a' : 'none'
                        }}>
                          {proj.status}
                        </span>
                      </div>

                      <h4 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#0f172a', margin: '0.2rem 0 0.4rem 0' }}>
                        {proj.title}
                      </h4>
                      <p style={{ color: '#64748b', fontSize: '0.88rem', margin: 0, lineHeight: '1.5' }}>
                        {proj.description.length > 120 ? `${proj.description.substring(0, 120)}...` : proj.description}
                      </p>
                    </div>

                    <div style={{ textAlign: 'right', minWidth: '180px' }}>
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Estimated Investment</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 900, color: '#0f172a', fontFamily: 'Outfit' }}>
                        {hasProposal ? `$${proj.proposal.totalEstimate.toLocaleString()}` : 'Calculating...'}
                      </div>
                      <div style={{ fontSize: '0.78rem', color: proj.status === 'Needs More Info' ? '#b45309' : '#059669', fontWeight: 700 }}>
                        {proj.status === 'Needs More Info' ? '⚠️ Clarification Needed' : isSigned ? '✓ SOW Signed' : hasProposal ? '✍️ SOW Ready to Sign' : '⏳ In Review'}
                      </div>
                    </div>

                    <div>
                      <Link
                        to={`/project/${proj.id}`}
                        className={proj.status === 'Needs More Info' ? 'btn-primary-blue' : 'btn-primary-blue'}
                        style={{
                          padding: '0.65rem 1.3rem',
                          fontSize: '0.88rem',
                          textDecoration: 'none',
                          backgroundColor: proj.status === 'Needs More Info' ? '#d97706' : undefined,
                          borderColor: proj.status === 'Needs More Info' ? '#d97706' : undefined
                        }}
                      >
                        {proj.status === 'Needs More Info' ? "Answer Guru's Questions ⚠️" : "Inspect What It Takes \u2192"}
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientDashboard;
